/**
 * ADMIN PERMISSIONS SYSTEM
 * 
 * Comprehensive permission checking and management for the multi-admin RBAC system.
 * Provides granular page-level and feature-level access control.
 */

import { supabase } from './supabase'
import { getSessionHeaders } from './serverSession';

// API helper for permission operations - NO getServiceRoleClient() in browser
async function callRolesAPI(operation: string, data: Record<string, any> = {}): Promise<any> {
 const response = await fetch('/api/admin/roles', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
 body: JSON.stringify({ operation, data })
 });
 return response.json();
}

export interface Permission {
 id: string
 permission_key: string
 display_name: string
 description: string
 category: string
}

export interface Role {
 id: string
 role_name: string
 display_name: string
 description: string
 is_system_role: boolean
}

export interface AdminUserWithPermissions {
 id: string
 email: string
 full_name?: string
 role: string
 is_active: boolean
 permissions: string[]
}

/**
 * Get all permissions for a specific role
 */
export async function getRolePermissions(roleName: string): Promise<string[]> {
 try {
 const { data: roleData, error: roleError } = await supabase
 .from('admin_roles')
 .select('id')
 .eq('role_name', roleName)
 .single()

 if (roleError || !roleData) {
 console.error('Error fetching role:', roleError)
 return []
 }

 const { data: permissionsData, error: permissionsError } = await supabase
 .from('role_permissions')
 .select(`
 admin_permissions (
 permission_key
 )
 `)
 .eq('role_id', roleData.id)

 if (permissionsError) {
 console.error('Error fetching permissions:', permissionsError)
 return []
 }

 return permissionsData.map((p: any) => p.admin_permissions.permission_key)
 } catch (err) {
 console.error('Error getting role permissions:', err)
 return []
 }
}

/**
 * Check if a user has a specific permission
 */
export async function hasPermission(userEmail: string, permissionKey: string): Promise<boolean> {
 try {
 // Use API endpoint - NO getServiceRoleClient() in browser
 const result = await callRolesAPI('check_permission', { userEmail, permissionKey });
 return result.hasPermission || false;
 } catch (err) {
 console.error('Error checking permission:', err)
 return false
 }
}

/**
 * Check if a user has any of the specified permissions
 */
export async function hasAnyPermission(userEmail: string, permissionKeys: string[]): Promise<boolean> {
 try {
 // Use API endpoint - NO getServiceRoleClient() in browser
 const result = await callRolesAPI('check_any_permission', { userEmail, permissionKeys });
 return result.hasPermission || false;
 } catch (err) {
 console.error('Error checking permissions:', err)
 return false
 }
}

/**
 * Check if a user has all of the specified permissions
 */
export async function hasAllPermissions(userEmail: string, permissionKeys: string[]): Promise<boolean> {
 try {
 // Use API endpoint - NO getServiceRoleClient() in browser
 const result = await callRolesAPI('check_all_permissions', { userEmail, permissionKeys });
 return result.hasPermission || false;
 } catch (err) {
 console.error('Error checking permissions:', err)
 return false
 }
}

/**
 * Get admin user with all their permissions
 */
export async function getAdminUserWithPermissions(userEmail: string): Promise<AdminUserWithPermissions | null> {
 try {
 // Use API endpoint - NO getServiceRoleClient() in browser
 const result = await callRolesAPI('get_admin_with_permissions', { userEmail });
 return result.user || null;
 } catch (err) {
 console.error('Error getting admin user with permissions:', err)
 return null
 }
}

/**
 * Get all available permissions
 */
export async function getAllPermissions(): Promise<Permission[]> {
 try {
 const { data, error } = await supabase
 .from('admin_permissions')
 .select('*')
 .order('category', { ascending: true })
 .order('display_name', { ascending: true })

 if (error) {
 console.error('Error fetching permissions:', error)
 return []
 }

 return data || []
 } catch (err) {
 console.error('Error getting all permissions:', err)
 return []
 }
}

/**
 * Get all available roles
 */
export async function getAllRoles(): Promise<Role[]> {
 try {
 const { data, error } = await supabase
 .from('admin_roles')
 .select('*')
 .order('role_name', { ascending: true })

 if (error) {
 console.error('Error fetching roles:', error)
 return []
 }

 return data || []
 } catch (err) {
 console.error('Error getting all roles:', err)
 return []
 }
}

/**
 * Page-level permission requirements
 * Maps admin dashboard page IDs to required permissions
 */
export const PAGE_PERMISSIONS: Record<string, string[]> = {
 // User Management
 'users': ['manage_users'],
 'dormant-purge': ['manage_users'],
 'kyc': ['manage_kyc'],
 'admin-kyc-completion': ['manage_kyc'],
 'kyc-cleanup': ['manage_kyc'],
 
 // Financial Management
 'payments': ['manage_payments'],
 'payment-integrity': ['view_financial_reports'],
 'payment-integrity-v2': ['view_financial_reports'],
 'commissions': ['manage_commissions'],
 'withdrawal-management': ['manage_withdrawals'],
 'commission-conversions': ['manage_commissions'],
 'commission-audit': ['view_financial_reports'],
 'share-balance-audit': ['view_financial_reports'],
 'username-audit': ['view_users'],
 'master-audit': ['view_financial_reports', 'view_audit_logs'],
 'financial-audit': ['view_financial_reports'],
 'reports': ['view_financial_reports'],
 'transaction-management': ['manage_payments'],
 'funds': ['manage_payments'],
 'wallets': ['manage_wallets'],
 'share-transfer': ['manage_payments'],
 
 // Content Management
 'certificates': ['manage_certificates'],
 'cert-test': ['manage_certificates'],
 'sponsors': ['manage_sponsors'],
 'downline': ['manage_sponsors'],
 'competitions': ['manage_competitions'],
 'meetings': ['manage_support'],
 'gallery': ['manage_gallery'],
 'marketing': ['manage_marketing'],
 
 // Communication
 'list-manager': ['send_mass_emails'],
 'mass-email': ['send_mass_emails'],
 'announcements': ['send_mass_emails'],
 
 // Support & Training
 'support': ['manage_support'],
 'training': ['manage_training'],
 
 // System & Admin
 'todo': [], // Available to all admins - Task Dashboard
 'menu-order': [], // Available to all admins
 'audit': ['view_audit_logs'],
 'test-audit': ['access_debug'],
 'phase-test': ['access_debug'],
 'debug': ['access_debug'],
 'admin-users': ['manage_admins'],
 'role-permissions': ['manage_admins'], // Role & Permission Manager
 'user-assistance': ['manage_admins'], // v4.7.66: master-password impersonation tool — gated to admin-managers only

 // ACRES (Aureus Community Reward & Engagement System)
 'acres': ['manage_acres_tasks', 'moderate_acres_tasks', 'manage_acres_draws', 'manage_acres_achievements', 'manage_acres_banner', 'view_acres_analytics'],
 'acres-tasks': ['manage_acres_tasks'],
 'acres-moderation': ['moderate_acres_tasks'],
 'acres-draws': ['manage_acres_draws'],
 'acres-achievements': ['manage_acres_achievements'],
 'acres-banner': ['manage_acres_banner'],
 'acres-analytics': ['view_acres_analytics']
}

/**
 * Check if user can access a specific page
 */
export async function canAccessPage(userEmail: string, pageId: string): Promise<boolean> {
 const requiredPermissions = PAGE_PERMISSIONS[pageId]
 
 // If no permissions required, allow access
 if (!requiredPermissions || requiredPermissions.length === 0) {
 return true
 }

 // Check if user has any of the required permissions
 return await hasAnyPermission(userEmail, requiredPermissions)
}

/**
 * Get list of accessible pages for a user
 *
 * IMPORTANT: This function returns ALL pages that the user can access.
 * - Pages NOT in PAGE_PERMISSIONS are accessible to ALL admins (no special permissions required)
 * - Pages IN PAGE_PERMISSIONS require the user to have at least one of the specified permissions
 *
 * This ensures that new pages added to defaultTabs in AdminDashboard.tsx are automatically
 * accessible to all admins until specific permissions are configured in PAGE_PERMISSIONS.
 */
export async function getAccessiblePages(userEmail: string): Promise<string[]> {
 // NOTE: We cannot import defaultTabs from AdminDashboard.tsx here due to circular dependency
 // Instead, we return pages from PAGE_PERMISSIONS and let AdminDashboard.tsx handle the rest
 // by showing all pages that are NOT in PAGE_PERMISSIONS (which means they're accessible to all)

 const accessiblePages: string[] = []

 for (const [pageId, requiredPermissions] of Object.entries(PAGE_PERMISSIONS)) {
 if (requiredPermissions.length === 0) {
 accessiblePages.push(pageId)
 } else {
 const hasAccess = await hasAnyPermission(userEmail, requiredPermissions)
 if (hasAccess) {
 accessiblePages.push(pageId)
 }
 }
 }

 return accessiblePages
}

